// ============================================================
// Services.js
// Place in: frontend/src/pages/Services.js
// CSS file:  frontend/src/styles/services.css
// ============================================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Login from './Login';
import '../styles/services.css';

function Services() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [activeService, setActiveService] = useState(0);
    const [countersStarted, setCountersStarted] = useState(false);
    const statsRef = useRef(null);
    const isAuthenticated = !!localStorage.getItem('token');

    // Always open Services page from the top
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll animations
    useEffect(() => {
        const targets = document.querySelectorAll('.svc-animate, .svc-animate-left, .svc-animate-right, .svc-animate-up');
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => {
                if (e.isIntersecting) e.target.classList.add('svc-visible');
            }),
            { threshold: 0.1 }
        );
        targets.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // Counter animation
    useEffect(() => {
        if (!statsRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !countersStarted) {
                    setCountersStarted(true);
                    animateCounters();
                }
            },
            { threshold: 0.3 }
        );
        observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, [countersStarted]);

    const animateCounters = () => {
        const counters = document.querySelectorAll('.svc-counter');
        counters.forEach(counter => {
            const target = parseInt(counter.getAttribute('data-target'));
            const duration = 1800;
            const step = target / (duration / 16);
            let current = 0;
            const timer = setInterval(() => {
                current += step;
                if (current >= target) {
                    current = target;
                    clearInterval(timer);
                }
                counter.textContent = Math.floor(current).toLocaleString() + (counter.getAttribute('data-suffix') || '');
            }, 16);
        });
    };

    // ── Services Data ──────────────────────────────────────
    const services = [
        {
            id: 'diy',
            icon: '🎨',
            badge: 'Self-Service',
            badgeColor: 'blue',
            title: 'DIY Self-Service',
            tagline: 'Build It Your Way',
            description: 'Get full access to our powerful no-code platform and build your WhatsApp chatbot at your own pace. Perfect for tech-savvy entrepreneurs and small teams who want complete control.',
            idealFor: ['Startups & solo founders', 'Tech-savvy teams', 'Quick prototyping', 'Budget-conscious businesses'],
            deliverables: [
                { icon: '🎯', text: 'Access to Visual Flow Builder with unlimited nodes' },
                { icon: '📋', text: '50+ pre-built industry templates to start fast' },
                { icon: '📚', text: 'Full documentation & video tutorial library' },
                { icon: '💬', text: 'Community forum & email support' },
                { icon: '🔑', text: 'You manage your own OpenAI API credits' },
                { icon: '⚡', text: 'Deploy in under 30 minutes' },
            ],
            highlight: '< 30 min setup',
            highlightSub: 'Average time to first live bot',
            cta: 'Start Building Free',
            ctaRoute: '/pricing',
        },
        {
            id: 'guided',
            icon: '🤝',
            badge: 'Most Popular',
            badgeColor: 'green',
            title: 'Guided Setup',
            tagline: 'We Help You Build',
            description: 'Our experts work alongside you to design, build, and launch your WhatsApp chatbot. You stay in control while we handle the technical complexity and best-practice guidance.',
            idealFor: ['Growing businesses', 'Non-technical founders', 'Teams with existing workflows', 'Businesses needing CRM integration'],
            deliverables: [
                { icon: '🏗️', text: 'Dedicated onboarding specialist assigned to you' },
                { icon: '📱', text: 'WhatsApp Business API & Developer Account setup' },
                { icon: '🧠', text: 'AI training & custom prompt engineering' },
                { icon: '🔗', text: 'CRM / third-party API integration guidance' },
                { icon: '🤖', text: 'OpenAI account setup (optional add-on)' },
                { icon: '📞', text: 'Priority support via phone, email & WhatsApp' },
            ],
            highlight: '48 hr',
            highlightSub: 'Avg. time to fully live bot',
            cta: 'Get Guided Setup',
            ctaRoute: '/contact',
            popular: true,
        },
        {
            id: 'turnkey',
            icon: '👨‍💻',
            badge: 'Full Service',
            badgeColor: 'purple',
            title: 'Full Turnkey Solution',
            tagline: 'We Do Everything',
            description: 'Hand over the entire project to our team. From strategy and design to deployment and maintenance — we deliver a production-ready WhatsApp chatbot while you focus on your business.',
            idealFor: ['Enterprise clients', 'Agencies reselling to clients', 'Complex multi-flow systems', 'Businesses with zero technical bandwidth'],
            deliverables: [
                { icon: '🗺️', text: 'Complete conversation strategy & UX design' },
                { icon: '💻', text: 'Full custom flow development from scratch' },
                { icon: '📱', text: 'WhatsApp Business API setup end-to-end' },
                { icon: '🤖', text: 'OpenAI account, credits & fine-tuning included' },
                { icon: '🎯', text: 'Custom AI business prompt creation & training' },
                { icon: '🛡️', text: 'Dedicated account manager & ongoing maintenance' },
            ],
            highlight: '7 days',
            highlightSub: 'Avg. project delivery time',
            cta: 'Discuss Your Project',
            ctaRoute: '/contact',
        },
    ];

    const processSteps = [
        { num: '01', icon: '📞', title: 'Discovery Call', desc: 'We understand your business, goals, and the customer journeys you want to automate — no technical knowledge required from your side.' },
        { num: '02', icon: '🗺️', title: 'Flow Strategy', desc: 'Our team maps out the ideal conversation architecture — what triggers the bot, how it branches, when AI kicks in, and when humans take over.' },
        { num: '03', icon: '🔨', title: 'Build & Configure', desc: 'We build your flows in the visual builder, integrate your AI, connect your WhatsApp number, and set up all necessary APIs and webhooks.' },
        { num: '04', icon: '🧪', title: 'Test & Refine', desc: 'Rigorous end-to-end testing across real device types, edge cases, and unexpected inputs. We iterate until every conversation is flawless.' },
        { num: '05', icon: '🚀', title: 'Launch & Monitor', desc: 'Your bot goes live with real-time monitoring. We track performance, catch issues early, and optimise based on actual user conversations.' },
    ];

    const stats = [
        { target: 10000, suffix: '+', label: 'Businesses Served' },
        { target: 30, suffix: ' min', label: 'Average DIY Setup' },
        { target: 99, suffix: '.9%', label: 'Uptime SLA' },
        { target: 48, suffix: ' hrs', label: 'Guided Setup Delivery' },
    ];

    const testimonials = [
        {
            quote: 'The Turnkey service was incredible. Our entire WhatsApp automation was live in 5 days. The team understood our e-commerce flows better than we did.',
            name: 'Neha Gupta',
            role: 'Head of Digital',
            avatar: 'N',
            color: '#25D366',
            service: 'Turnkey Solution',
        },
        {
            quote: 'Guided Setup was exactly what we needed. We stayed in control of the bot but had experts guiding every step. Zero frustration, maximum results.',
            name: 'Arjun Kapoor',
            role: 'Founder',
            avatar: 'A',
            color: '#128C7E',
            service: 'Guided Setup',
        },
        {
            quote: 'I built my entire support bot in 20 minutes using the DIY templates. The documentation is actually readable by a non-technical person like me.',
            name: 'Sanya Malhotra',
            role: 'Owner',
            avatar: 'S',
            color: '#065f46',
            service: 'DIY Self-Service',
        },
    ];

    const addOns = [
        { icon: '🤖', title: 'OpenAI Setup & Credits', desc: 'We create and configure your OpenAI account, set API keys, and load initial credits. Hands-free AI from day one.' },
        { icon: '📊', title: 'CRM Integration', desc: 'Connect your chatbot to HubSpot, Zoho, Salesforce, or any custom CRM via webhooks and REST APIs.' },
        { icon: '🌐', title: 'Multi-Number Setup', desc: 'Running multiple brands or locations? We deploy and manage separate bot instances for each WhatsApp number.' },
        { icon: '🎓', title: 'Team Training Sessions', desc: 'Live 1-on-1 or group training for your team to manage, update, and analyse the bot independently going forward.' },
    ];

    return (
        <div className="svc-page">

            {/* ── NAVBAR ── */}
            <nav className={`ct-nav ${scrolled ? 'ct-nav-scrolled' : ''}`}>
                <div className="ct-nav-brand" onClick={() => navigate('/')}>
                    <img src="/logo.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                </div>
                <div className="ct-nav-links">
                    <Link to="/how-it-works" className="ct-nav-link">How It Works</Link>
                    <Link to="/features" className="ct-nav-link">Features</Link>
                    <Link to="/services" className="ct-nav-link" style={{ color: '#128C7E', fontWeight: 700 }}>Services</Link>
                    <Link to="/pricing" className="ct-nav-link">Pricing</Link>
                    <Link to="/contact" className="ct-nav-link">Contact</Link>
                </div>
                <div className="ct-nav-actions">
                    {isAuthenticated ? (
                        <button onClick={() => navigate('/dashboard')} className="ct-btn-primary">Dashboard →</button>
                    ) : (
                        <>
                            <button onClick={() => setLoginModalOpen(true)} className="ct-btn-ghost">Login</button>
                            <button onClick={() => navigate('/pricing')} className="ct-btn-primary">Get Started</button>
                        </>
                    )}
                </div>
                <button className="ct-nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    <span /><span /><span />
                </button>
            </nav>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="ct-nav-mobile">
                    <div className="ct-mobile-menu-header">
                        <div 
    className="ct-mobile-menu-brand" 
    onClick={() => { navigate('/'); setMobileMenuOpen(false); }} 
    style={{ gap: '2px', cursor: 'pointer' }}
>
    <img src="/logo 1.png" alt="ChatTech24" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
    <span style={{ color: '#D3D3D3', fontFamily: 'Sora, sans-serif', fontSize: '12px', fontWeight: '700' }}>ChatTech24</span>
</div>
                        <button className="ct-mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
                    </div>
                    <div className="ct-mobile-menu-links">
                        <Link to="/how-it-works" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon">⚙️</span><span>How It Works</span><span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/features" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon">✨</span><span>Features</span><span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/services" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon">🚀</span><span>Services</span><span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/pricing" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon">💎</span><span>Pricing</span><span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/contact" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon">📞</span><span>Contact</span><span className="ct-mobile-link-arrow">›</span>
                        </Link>
                    </div>
                    <div className="ct-mobile-menu-actions">
                        <button className="ct-mobile-btn-login" onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false); }}>👤 Login</button>
                        <button className="ct-mobile-btn-cta" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}>🚀 Get Started</button>
                    </div>
                    <div className="ct-mobile-menu-footer">
                        <span>🔒 Secure & Reliable</span><span>⚡ 99.9% Uptime</span>
                    </div>
                </div>
            )}

            {/* ── HERO HEADER ── */}
            <div className="svc-hero">
                <div className="svc-hero-orb svc-hero-orb-1" />
                <div className="svc-hero-orb svc-hero-orb-2" />
                <div className="svc-hero-orb svc-hero-orb-3" />
                <div className="svc-hero-inner">
                    <div className="svc-hero-badge">🚀 Our Services</div>
                    <h1 className="svc-hero-title">
                        Choose How You Want to<br />
                        <span className="svc-hero-title-accent">Launch & Grow</span>
                    </h1>
                    <p className="svc-hero-subtitle">
                        Whether you want full control, expert guidance, or a completely done-for-you solution — we have a service model built exactly for your business.
                    </p>
                    <div className="svc-hero-pills">
                        <span className="svc-pill">✅ No hidden charges</span>
                        <span className="svc-pill">✅ WhatsApp Business API included</span>
                        <span className="svc-pill">✅ AI-powered from day one</span>
                    </div>
                </div>
            </div>

            {/* ── STATS STRIP ── */}
            <div className="svc-stats-strip" ref={statsRef}>
                {stats.map((s, i) => (
                    <div key={i} className="svc-stat">
                        <div className="svc-stat-num">
                            <span className="svc-counter" data-target={s.target} data-suffix={s.suffix}>0{s.suffix}</span>
                        </div>
                        <div className="svc-stat-label">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── SERVICE SELECTOR TABS ── */}
            <div className="svc-tabs-wrap">
                <div className="svc-tabs">
                    {services.map((s, i) => (
                        <button
                            key={i}
                            className={`svc-tab ${activeService === i ? 'svc-tab-active' : ''}`}
                            onClick={() => setActiveService(i)}
                        >
                            <span className="svc-tab-icon">{s.icon}</span>
                            <span>{s.title}</span>
                            {s.popular && <span className="svc-tab-hot">HOT</span>}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── SERVICE DETAIL PANEL ── */}
            <div className="svc-detail-section">
                {services.map((svc, i) => (
                    <div key={i} className={`svc-detail-panel ${activeService === i ? 'svc-detail-active' : ''}`}>
                        <div className="svc-detail-left">
                            <div className={`svc-detail-badge svc-badge-${svc.badgeColor}`}>{svc.badge}</div>
                            <div className="svc-detail-icon-wrap">
                                <span className="svc-detail-icon">{svc.icon}</span>
                            </div>
                            <h2 className="svc-detail-title">{svc.title}</h2>
                            <p className="svc-detail-tagline">{svc.tagline}</p>
                            <p className="svc-detail-desc">{svc.description}</p>

                            <div className="svc-ideal-for">
                                <div className="svc-ideal-label">✅ Ideal For</div>
                                <div className="svc-ideal-tags">
                                    {svc.idealFor.map((t, ti) => (
                                        <span key={ti} className="svc-ideal-tag">{t}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="svc-detail-highlight">
                                <div className="svc-highlight-num">{svc.highlight}</div>
                                <div className="svc-highlight-sub">{svc.highlightSub}</div>
                            </div>

                            <button onClick={() => navigate(svc.ctaRoute)} className="svc-detail-cta">
                                {svc.cta} →
                            </button>
                        </div>

                        <div className="svc-detail-right">
                            <div className="svc-deliverables-card">
                                <div className="svc-deliverables-header">
                                    <span>📦</span>
                                    <span>What's Included</span>
                                </div>
                                {svc.deliverables.map((d, di) => (
                                    <div key={di} className="svc-deliverable-item" style={{ animationDelay: `${di * 0.08}s` }}>
                                        <span className="svc-deliverable-icon">{d.icon}</span>
                                        <span className="svc-deliverable-text">{d.text}</span>
                                        <span className="svc-deliverable-check">✓</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── ALL PLANS COMPARE CARDS ── */}
            <div className="svc-plans-section">
                <div className="svc-plans-inner">
                    <div className="svc-section-header svc-animate">
                        <div className="svc-section-badge">⚡ Side by Side</div>
                        <h2 className="svc-section-title">Compare All Service Options</h2>
                        <p className="svc-section-sub">Pick the one that matches your team, timeline, and budget</p>
                    </div>
                    <div className="svc-plans-grid">
                        {services.map((svc, i) => (
                            <div key={i} className={`svc-plan-card svc-animate ${svc.popular ? 'svc-plan-card-popular' : ''}`} style={{ animationDelay: `${i * 0.12}s` }}>
                                {svc.popular && <div className="svc-plan-popular-ribbon">⭐ Most Popular</div>}
                                <div className="svc-plan-card-header">
                                    <span className="svc-plan-card-icon">{svc.icon}</span>
                                    <h3 className="svc-plan-card-title">{svc.title}</h3>
                                    <p className="svc-plan-card-sub">{svc.tagline}</p>
                                </div>
                                <ul className="svc-plan-card-list">
                                    {svc.deliverables.map((d, di) => (
                                        <li key={di}>
                                            <span className="svc-plan-check">✓</span>
                                            <span>{d.text}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button onClick={() => navigate(svc.ctaRoute)} className={`svc-plan-btn ${svc.popular ? 'svc-plan-btn-primary' : 'svc-plan-btn-outline'}`}>
                                    {svc.cta} →
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── HOW WE WORK PROCESS ── */}
            <div className="svc-process-section">
                <div className="svc-process-inner">
                    <div className="svc-section-header svc-animate">
                        <div className="svc-section-badge">🔄 Our Process</div>
                        <h2 className="svc-section-title">How We Deliver Your Chatbot</h2>
                        <p className="svc-section-sub">A proven 5-step process used across every Guided & Turnkey project</p>
                    </div>
                    <div className="svc-process-track">
                        <div className="svc-process-line" />
                        {processSteps.map((step, i) => (
                            <div key={i} className={`svc-process-step svc-animate`} style={{ animationDelay: `${i * 0.14}s` }}>
                                <div className="svc-process-node">
                                    <div className="svc-process-num">{step.num}</div>
                                    <div className="svc-process-icon">{step.icon}</div>
                                </div>
                                <div className="svc-process-content">
                                    <h3 className="svc-process-title">{step.title}</h3>
                                    <p className="svc-process-desc">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── ADD-ONS ── */}
            <div className="svc-addons-section">
                <div className="svc-addons-inner">
                    <div className="svc-section-header svc-animate">
                        <div className="svc-section-badge">➕ Add-Ons</div>
                        <h2 className="svc-section-title">Supercharge Any Service</h2>
                        <p className="svc-section-sub">Available as optional upgrades on any service plan</p>
                    </div>
                    <div className="svc-addons-grid">
                        {addOns.map((addon, i) => (
                            <div key={i} className="svc-addon-card svc-animate" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="svc-addon-icon">{addon.icon}</div>
                                <h3 className="svc-addon-title">{addon.title}</h3>
                                <p className="svc-addon-desc">{addon.desc}</p>
                                <button onClick={() => navigate('/contact')} className="svc-addon-btn">Ask About This →</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

          {/* ── TESTIMONIALS ── */}
<div className="svc-testi-section">
    <div className="svc-testi-inner">
        <div className="svc-section-header svc-animate">
            <div className="svc-section-badge">💬 Real Results</div>
            <h2 className="svc-section-title" style={{ color: 'white' }}>What Our Clients Say</h2>
            <p className="svc-section-sub" style={{ color: 'rgba(255,255,255,0.7)' }}>From all three service tiers — real feedback, real outcomes</p>
        </div>

        {/* ── MARQUEE CAROUSEL ── */}
        <div className="svc-testi-marquee-wrap">
            <div className="svc-testi-marquee-track">
                {[...testimonials, ...testimonials].map((t, i) => (
                    <div key={i} className="svc-testi-card">
                        <div className="svc-testi-service-tag">{t.service}</div>
                        <div className="svc-testi-stars">★★★★★</div>
                        <p className="svc-testi-quote">"{t.quote}"</p>
                        <div className="svc-testi-author">
                            <div className="svc-testi-avatar" style={{ background: t.color }}>{t.avatar}</div>
                            <div>
                                <div className="svc-testi-name">{t.name}</div>
                                <div className="svc-testi-role">{t.role}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

    </div>
</div>

            {/* ── DECISION HELPER ── */}
            <div className="svc-decision-section">
                <div className="svc-decision-inner">
                    <div className="svc-section-header svc-animate">
                        <div className="svc-section-badge">🤔 Not Sure?</div>
                        <h2 className="svc-section-title">Which Service is Right for You?</h2>
                    </div>
                    <div className="svc-decision-grid">
                        <div className="svc-decision-card svc-animate">
                            <div className="svc-decision-emoji">💡</div>
                            <h3 className="svc-decision-title">Choose DIY if…</h3>
                            <ul className="svc-decision-list">
                                <li>You're comfortable with digital tools</li>
                                <li>You want full ownership from day one</li>
                                <li>Your flows are relatively straightforward</li>
                                <li>You have time to learn and iterate</li>
                            </ul>
                            <button onClick={() => navigate('/pricing')} className="svc-decision-btn svc-decision-btn-blue">Start DIY →</button>
                        </div>
                        <div className="svc-decision-card svc-decision-card-featured svc-animate" style={{ animationDelay: '0.1s' }}>
                            <div className="svc-decision-emoji">🤝</div>
                            <h3 className="svc-decision-title">Choose Guided if…</h3>
                            <ul className="svc-decision-list">
                                <li>You want expert help but stay in control</li>
                                <li>You have existing tools to integrate</li>
                                <li>Your flows need some custom logic</li>
                                <li>You want to learn as you build</li>
                            </ul>
                            <button onClick={() => navigate('/contact')} className="svc-decision-btn svc-decision-btn-green">Get Guided →</button>
                        </div>
                        <div className="svc-decision-card svc-animate" style={{ animationDelay: '0.2s' }}>
                            <div className="svc-decision-emoji">👨‍💻</div>
                            <h3 className="svc-decision-title">Choose Turnkey if…</h3>
                            <ul className="svc-decision-list">
                                <li>You want zero involvement in the tech</li>
                                <li>You need complex multi-flow systems</li>
                                <li>You have a tight launch deadline</li>
                                <li>You want it perfect from day one</li>
                            </ul>
                            <button onClick={() => navigate('/contact')} className="svc-decision-btn svc-decision-btn-purple">Go Turnkey →</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── CTA BOTTOM ── */}
            <div className="svc-cta">
                <div className="svc-cta-orb svc-cta-orb-1" />
                <div className="svc-cta-orb svc-cta-orb-2" />
                <h2 className="svc-cta-title">Ready to Automate Your WhatsApp?</h2>
                <p className="svc-cta-sub">Join 10,000+ businesses. No credit card required.</p>
                <div className="svc-cta-btns">
                    <button onClick={() => navigate('/pricing')} className="svc-btn-white">🚀 Start Free Trial</button>
                    <button onClick={() => navigate('/contact')} className="svc-btn-outline">📞 Talk to Our Team</button>
                </div>
            </div>

            {/* ── FOOTER ── */}
            <footer className="ct-footer">
                <div className="ct-container">
                    <div className="ct-footer-grid">
                        <div>
                            <div className="ct-nav-brand" onClick={() => navigate('/')} style={{ gap: '2px', cursor: 'pointer' }}>
                                <img src="/logo 1.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                                <span style={{ color: '#D3D3D3', fontFamily: 'Cooper Black', fontSize: '13px', fontWeight: '700' }}>Chat Tech 24</span>
                            </div>
                            <p className="ct-footer-tagline">AI-powered WhatsApp automation platform for modern businesses.</p>
                            <div className="ct-footer-social">
                                <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer" className="ct-footer-social-link">🌐 Website</a>
                                <Link to="/contact" className="ct-footer-social-link">✉️ Contact</Link>
                            </div>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Product</div>
                            <Link to="/pricing">Pricing</Link>
                            <Link to="/features">Features</Link>
                            <Link to="/services">Services</Link>
                            <Link to="/how-it-works">How It Works</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Company</div>
                            <a href="https://vedvika.com/#about" target="_blank" rel="noopener noreferrer">About Vedvika</a>
                            <Link to="/contact">Contact Us</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Support</div>
                            <Link to="/how-it-works">Documentation</Link>
                            <Link to="/contact">Talk to Sales</Link>
                            <a href="mailto:support@chattech24.com">Email Support</a>
                        </div>
                    </div>
                    <div className="ct-footer-bottom">
                        <p className="ct-footer-copy">
                            © 2026 ChatTech24 by <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer">Vedvika Technologies</a>. All rights reserved.
                        </p>
                        <div className="ct-footer-legal">
                            <a href="https://vedvika.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            <a href="https://vedvika.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        </div>
                    </div>
                </div>
            </footer>

            <Login isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
        </div>
    );
}

export default Services;